import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Newspaper, Calendar } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HERO_IMG } from "./Aprende";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

export default function Noticiero() {
  const { isAdmin } = useAuth();
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const hero = articles?.find((a) => a.featured) || articles?.[0];
  const rest = articles?.filter((a) => a.id !== hero?.id) || [];

  return (
    <div>
      <section className="relative overflow-hidden bg-nasa-navy">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-r from-nasa-navy via-nasa-navy/80 to-nasa-navy/30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-36 pb-20 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-6"><span className="eyebrow text-white/90">ACTUALIDAD CIENTÍFICA</span></div>
            <h1 className="font-hero font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white">El Noticiero Científico</h1>
            <p className="mt-7 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed">Las noticias y descubrimientos más relevantes del mundo de la ciencia.</p>
          </div>
          {isAdmin && (
            <Link to="/admin/articulo/nuevo" data-testid="add-article-btn" className="inline-flex items-center gap-2 bg-nasa-blue text-white font-semibold px-5 py-2.5 rounded-full hover:bg-nasa-navy transition-colors">
              <Plus size={16} /> Nuevo artículo
            </Link>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        {articles === null ? (
          <p className="text-ink-500">Cargando…</p>
        ) : articles.length === 0 ? (
          <div className="border border-dashed border-paper-300 bg-paper-50 p-16 text-center">
            <Newspaper className="mx-auto text-ink-400" size={44} />
            <p className="text-ink-700 mt-4 font-medium">Aún no hay noticias publicadas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {hero && (
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-8">
                <Link to={`/noticiero/${hero.id}`} data-testid={`article-hero-${hero.id}`} className="group block h-full border border-paper-200 hover:border-nasa-blue transition-colors overflow-hidden relative min-h-[400px]">
                  {hero.cover_image ? (
                    <img src={mediaUrl(hero.cover_image)} alt={hero.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : <div className="absolute inset-0 science-grid" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-nasa-navy via-nasa-navy/55 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-8 text-white">
                    <span className="eyebrow text-white/90 flex items-center gap-2 mb-3"><Calendar size={13} /> {fmtDate(hero.created_at)}</span>
                    <h2 className="font-serif-sci font-bold text-3xl lg:text-4xl leading-tight max-w-2xl">{hero.title}</h2>
                    {hero.excerpt && <p className="text-white/85 mt-3 max-w-xl line-clamp-2">{hero.excerpt}</p>}
                    <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-white group-hover:gap-2.5 transition-all">Leer artículo <ArrowRight size={15} /></span>
                  </div>
                </Link>
              </motion.div>
            )}

            <div className="md:col-span-4 flex flex-col gap-6">
              {rest.slice(0, 2).map((a, i) => (
                <motion.div key={a.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * (i + 1) }} className="flex-1">
                  <Link to={`/noticiero/${a.id}`} data-testid={`article-card-${a.id}`} className="group block h-full bg-white border border-paper-200 hover:border-nasa-blue transition-colors overflow-hidden">
                    <div className="relative h-32 bg-paper-100 overflow-hidden">
                      {a.cover_image ? <img src={mediaUrl(a.cover_image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="science-grid w-full h-full" />}
                    </div>
                    <div className="p-5">
                      <span className="eyebrow text-ink-700 text-[10px]">{fmtDate(a.created_at)}</span>
                      <h3 className="font-serif-sci font-bold text-xl mt-1 text-nasa-blue group-hover:text-nasa-bright transition-colors line-clamp-2 leading-snug">{a.title}</h3>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {rest.slice(2).map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="md:col-span-4">
                <Link to={`/noticiero/${a.id}`} data-testid={`article-card-${a.id}`} className="group block h-full bg-white border border-paper-200 hover:border-nasa-blue transition-colors overflow-hidden">
                  <div className="relative h-44 bg-paper-100 overflow-hidden">
                    {a.cover_image ? <img src={mediaUrl(a.cover_image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="science-grid w-full h-full" />}
                    {!a.published && <span className="absolute top-3 left-3 eyebrow text-[9px] bg-nasa-blue text-white px-2 py-1">BORRADOR</span>}
                  </div>
                  <div className="p-6">
                    <span className="eyebrow text-ink-700 text-[10px]">{fmtDate(a.created_at)}</span>
                    <h3 className="font-serif-sci font-bold text-xl mt-1.5 text-nasa-blue group-hover:text-nasa-bright transition-colors line-clamp-2 leading-snug">{a.title}</h3>
                    {a.excerpt && <p className="text-ink-700 text-sm mt-2 line-clamp-2">{a.excerpt}</p>}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
